import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import { createApi } from '@reduxjs/toolkit/query/react';

import { User } from '@/features/auth/types';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

// The BFF's user endpoint: the session cookie is HttpOnly, so this is the only
// window into who is signed in. Claims come from Identity Server's profile
// scope; anything missing falls back to the name (the login email).
interface CurrentUserResponse {
  name: string | null;
  claims: { type: string; value: string }[];
}

const claimValue = (response: CurrentUserResponse, type: string) =>
  response.claims.find(claim => claim.type === type)?.value ?? '';

const toUser = (response: CurrentUserResponse): User => {
  const userName = response.name ?? claimValue(response, 'name');
  const firstName = claimValue(response, 'given_name');
  const middleName = claimValue(response, 'middle_name');
  const lastName = claimValue(response, 'family_name');

  return {
    id: claimValue(response, 'sub'),
    userName,
    email: claimValue(response, 'email') || userName,
    emailConfirmed: claimValue(response, 'email_verified') === 'true',
    firstName,
    middleName,
    lastName,
    fullName: [lastName, firstName, middleName].filter(Boolean).join(' ') || userName,
    phoneNumber: null,
    roles: response.claims.filter(claim => claim.type === 'role').map(claim => claim.value),
  };
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: axiosBaseQuery({ baseUrl: '' }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getCurrentUser: builder.query<User, void>({
      query: () => ({
        url: '/user',
      }),
      transformResponse: toUser,
    }),
  }),
});

export const { useLazyGetCurrentUserQuery } = authApi;
